import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchAdminBillingAccountQueryFn } from 'app/queries/billing/createFetchAdminBillingAccountQueryFn';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './BillingAccountRate/styles/Content';

type TProps = {
  billingAccountId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AdminBillingAccountRateModal = ({ billingAccountId, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const queryClient = useQueryClient();
  const [editedRate, setRate] = useState<number | string>();
  const [ratePlan, setRatePlan] = useState('');
  const [editedFixed, setFixed] = useState<boolean>();

  const billingAccountQuery = useQuery({
    queryKey: billingKeys.adminAccount(billingAccountId),
    queryFn: createFetchAdminBillingAccountQueryFn(call),
  });
  const billingAccount = billingAccountQuery.data;
  const rate = editedRate ?? Number(billingAccount?.tierRate ?? 0);
  const fixed = editedFixed ?? billingAccount?.fixedRate ?? false;

  const changeRatePlan = async (value: string) => {
    setRatePlan(value);
    if (value === 'freelancer') {
      setRate(13.5);
    }
    if (value === 'pro') {
      setRate(12);
    }
    if (value === 'agency') {
      setRate(11);
    }
    if (value === 'business') {
      setRate(10);
    }
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      call(() =>
        AdminService.setBillingAccountRate({ baId: billingAccountId, requestBody: { fixed, rate: Number(rate) } }),
      ),
    onSuccess: (result) => {
      if (result?.error) {
        toast.error(result.message ?? result.error);
        return;
      }
      closeModal();
      onSuccess();
    },
    onError: notifyApiCallError,
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: billingKeys.adminAccount(billingAccountId) }),
        queryClient.invalidateQueries({ queryKey: billingKeys.tierRatesHistoryByAccount(billingAccountId) }),
      ]),
  });

  const isLoading = billingAccountQuery.isLoading || saveMutation.isPending;

  return (
    <ModalWrapper width="400px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>AutoScript Rate Editor</ModalTitle>
      <Content>
        <div className="input-wrapper">
          <div className="label">Billing Account</div>
          <div className="name">{billingAccount?.name}</div>
        </div>

        <div className="input-wrapper">
          <div className="label">Organization</div>
          <div className="name">{billingAccount?.organization?.name}</div>
        </div>

        <div className="input-wrapper">
          <div className="label">Fixed rate</div>
          <Switch className="switch" checked={fixed} onChange={() => setFixed(!fixed)} />
        </div>

        <div className="input-wrapper">
          <div className="label">Rate plan</div>
          <Select
            value={ratePlan}
            options={[
              { value: '', label: 'Starter' },
              { value: 'freelancer', label: 'Freelancer' },
              { value: 'pro', label: 'Pro' },
              { value: 'agency', label: 'Agency' },
              { value: 'business', label: 'Business' },
            ]}
            onChange={(_, value) => changeRatePlan(value)}
            label="Team"
            required
            name="role"
          />
        </div>

        <div className="input-wrapper">
          <div className="label">Price per transcription audio hour</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              setRatePlan('');
              if (!value.length) {
                setRate(0);
                return;
              }
              setRate(value);
              if (value[value.length - 1] === '.') {
                setRate(value);
                return;
              }
              if (value.includes('.')) {
                const afterDotItemsCount = value.split('.')[1].length;
                if (afterDotItemsCount > 2) {
                  setRate(Number(value).toFixed(2));
                  return;
                }
              }
              if (Number(value)) {
                setRate(Number(value));
                return;
              }
              setRate(0);
            }}
            label="Price per transcription audio hour"
            value={rate}
          />
        </div>

        {billingAccountQuery.isError ? (
          <ErrorMessage error="Failed to load the billing account. Please try again." />
        ) : null}

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isLoading || !billingAccountQuery.isSuccess} onClick={() => saveMutation.mutate()}>
            Save
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
